"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import { api } from "@yfp/lib/api-client";
import { DEFAULT_YARD_TYPE } from "@yfp/constants/new-project-flow";
import { useAuth } from "./AuthContext";
import type {
  AssetType,
  YfpProject,
  YardType,
  YfpGenerationResult,
  YfpYardSpace,
} from "@yfp/types";
import type { StyleReferenceSummary } from "@yfp/lib/services/style-references";
import type {
  GeneratedConceptItem,
  SelectedYardFeature,
} from "@yfp/lib/services/contractors/types";

export type ImageSource = "satellite" | "ground-level";

const FLOW_STORAGE_KEY = "yfp.flow";

type PersistedFlow = {
  clientName: string;
  clientEmail: string;
  address: string;
  yardType: YardType | null;
  sourceImageUrl: string | null;
  imageSource: ImageSource | null;
  yardFeatures: SelectedYardFeature[];
  conceptPrompt: string;
  selectedStyleReference: StyleReferenceSummary | null;
  /** The yard space currently being built; the backend `yardSpaceId`. */
  spaceId: string | null;
  spaceLabel: string | null;
};

const EMPTY_FLOW: PersistedFlow = {
  clientName: "",
  clientEmail: "",
  address: "",
  // The style step reads `yardType` before generation, so it must be populated
  // from the start.
  yardType: DEFAULT_YARD_TYPE,
  sourceImageUrl: null,
  imageSource: null,
  yardFeatures: [],
  conceptPrompt: "",
  selectedStyleReference: null,
  spaceId: null,
  spaceLabel: null,
};

function loadPersistedFlow(): PersistedFlow | null {
  if (typeof window === "undefined") return null;
  const raw = window.sessionStorage.getItem(FLOW_STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as PersistedFlow;
  } catch {
    return null;
  }
}

function savePersistedFlow(flow: PersistedFlow): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(FLOW_STORAGE_KEY, JSON.stringify(flow));
  } catch {
    /* quota or serialization failure — non-fatal */
  }
}

export type GenerationResultAssetInput = {
  assetId: string;
  assetType: AssetType;
};

export type UpdateGenerationResultPatch = {
  status?: YfpGenerationResult["status"];
  jobId?: string;
  generatedImageUrl?: string;
  numEdits?: number;
  resultAssets?: GenerationResultAssetInput[];
};

export type CreateGenerationResultOptions = {
  styleReferenceId?: string;
  parentGenerationResultId?: string;
  prompt?: string;
  conversationId?: string;
  yardFeatures?: string[];
  /** The yard space this generation belongs to. */
  yardSpaceId?: string;
  /** The space's before-image, persisted as the result's source image. */
  sourceImageUrl?: string;
};

export type GeneratedVersion = {
  id: string;
  generatedImageUrl: string;
  styleImageUrl?: string;
  styleName?: string;
  createdAt: string;
  prompt?: string;
  parentVersionId?: string;
  elements?: GeneratedConceptItem[];
  materials?: GeneratedConceptItem[];
  plants?: GeneratedConceptItem[];
  /** Space this version belongs to; drives the space switcher on results. */
  spaceId?: string;
  spaceLabel?: string;
  /** The before-image for this space (per-space, not project-wide). */
  beforeImageUrl?: string | null;
};

type FlowContextType = {
  /**
   * `true` once the provider has finished reading the persisted flow state
   * from `sessionStorage`. Effects that branch on flow state (e.g. starting a
   * generation) must wait for this before deciding the state is "missing"
   * vs. "still loading".
   */
  hasHydrated: boolean;
  clientName: string;
  setClientName: (value: string) => void;
  clientEmail: string;
  setClientEmail: (value: string) => void;
  address: string;
  setAddress: (value: string) => void;
  yardType: YardType | null;
  setYardType: (value: YardType | null) => void;
  sourceImageUrl: string | null;
  setSourceImageUrl: Dispatch<SetStateAction<string | null>>;
  imageSource: ImageSource | null;
  setImageSource: (value: ImageSource | null) => void;
  yardFeatures: SelectedYardFeature[];
  setYardFeatures: (value: SelectedYardFeature[]) => void;
  conceptPrompt: string;
  setConceptPrompt: (value: string) => void;
  selectedStyleReference: StyleReferenceSummary | null;
  setSelectedStyleReference: (value: StyleReferenceSummary | null) => void;
  spaceId: string | null;
  setSpaceId: (value: string | null) => void;
  spaceLabel: string | null;
  setSpaceLabel: (value: string | null) => void;
  /** The space currently shown on the results page. */
  activeSpaceId: string | null;
  setActiveSpaceId: (value: string | null) => void;
  /**
   * Start a new space on the SAME project: keeps client + address + project,
   * clears the per-space inputs (photo, features, style, selection) and routes
   * the contractor back through photo → features → style → generate.
   */
  startNewSpace: () => void;
  generatedImageUrl: string | null;
  setGeneratedImageUrl: (value: string | null) => void;
  generatedVersions: GeneratedVersion[];
  setGeneratedVersions: (value: GeneratedVersion[]) => void;
  addGeneratedVersion: (version: GeneratedVersion) => void;
  selectedVersionIndex: number | null;
  setSelectedVersionIndex: (index: number | null) => void;
  resetFlow: () => void;

  currentProjectId: string | null;
  currentProject: YfpProject | null;
  currentGenerationResult: YfpGenerationResult | null;
  isCreatingProject: boolean;
  createProject: () => Promise<YfpProject>;
  createYardSpace: (
    projectId: string,
    input: { name: string; yardType: YardType },
  ) => Promise<YfpYardSpace>;
  renameYardSpace: (yardSpaceId: string, name: string) => Promise<YfpYardSpace>;
  createGenerationResult: (
    projectId: string,
    options?: CreateGenerationResultOptions,
  ) => Promise<YfpGenerationResult>;
  updateGenerationResult: (
    id: string,
    patch: UpdateGenerationResultPatch,
  ) => Promise<YfpGenerationResult>;
  fetchProject: (projectId: string) => Promise<YfpProject>;
  setActiveProject: (project: YfpProject) => void;
};

const FlowContext = createContext<FlowContextType | null>(null);

function upsertGenerationResult(
  project: YfpProject | null,
  result: YfpGenerationResult,
): YfpProject | null {
  if (!project || project.id !== result.projectId) return project;
  const existing = project.generationResults ?? [];
  const index = existing.findIndex((r) => r.id === result.id);
  const generationResults =
    index >= 0
      ? [...existing.slice(0, index), result, ...existing.slice(index + 1)]
      : [...existing, result];
  return { ...project, generationResults };
}

export function FlowProvider({ children }: { children: ReactNode }) {
  const { contractor } = useAuth();

  const [flow, setFlow] = useState<PersistedFlow>(EMPTY_FLOW);
  const [hasHydrated, setHasHydrated] = useState(false);

  const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(
    null,
  );
  const [generatedVersions, setGeneratedVersions] = useState<
    GeneratedVersion[]
  >([]);
  const [selectedVersionIndex, setSelectedVersionIndex] = useState<
    number | null
  >(null);
  const [activeSpaceId, setActiveSpaceId] = useState<string | null>(null);
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(null);
  const [currentProject, setCurrentProject] = useState<YfpProject | null>(null);
  const [currentGenerationResult, setCurrentGenerationResult] =
    useState<YfpGenerationResult | null>(null);
  const [isCreatingProject, setIsCreatingProject] = useState(false);

  // Reading sessionStorage in a useState initializer would cause SSR
  // hydration mismatches, so we hydrate once on mount and mirror every
  // subsequent change back to storage.
  useEffect(() => {
    const stored = loadPersistedFlow();
    if (stored)
      setFlow({
        ...EMPTY_FLOW,
        ...stored,
        yardType: stored.yardType ?? EMPTY_FLOW.yardType,
      });
    setHasHydrated(true);
  }, []);

  useEffect(() => {
    if (!hasHydrated) return;
    savePersistedFlow(flow);
  }, [flow, hasHydrated]);

  const setters = useMemo(
    () => ({
      setClientName: (value: string) =>
        setFlow((prev) => ({ ...prev, clientName: value })),
      setClientEmail: (value: string) =>
        setFlow((prev) => ({ ...prev, clientEmail: value })),
      setAddress: (value: string) =>
        setFlow((prev) => ({ ...prev, address: value })),
      setYardType: (value: YardType | null) =>
        setFlow((prev) => ({ ...prev, yardType: value })),
      setImageSource: (value: ImageSource | null) =>
        setFlow((prev) => ({ ...prev, imageSource: value })),
      setYardFeatures: (value: SelectedYardFeature[]) =>
        setFlow((prev) => ({ ...prev, yardFeatures: value })),
      setConceptPrompt: (value: string) =>
        setFlow((prev) => ({ ...prev, conceptPrompt: value })),
      setSelectedStyleReference: (value: StyleReferenceSummary | null) =>
        setFlow((prev) => ({ ...prev, selectedStyleReference: value })),
      setSpaceId: (value: string | null) =>
        setFlow((prev) => ({ ...prev, spaceId: value })),
      setSpaceLabel: (value: string | null) =>
        setFlow((prev) => ({ ...prev, spaceLabel: value })),
      setSourceImageUrl: ((value) =>
        setFlow((prev) => ({
          ...prev,
          sourceImageUrl:
            typeof value === "function" ? value(prev.sourceImageUrl) : value,
        }))) as Dispatch<SetStateAction<string | null>>,
    }),
    [],
  );

  const addGeneratedVersion = useCallback((version: GeneratedVersion) => {
    setGeneratedVersions((prev) => [...prev, version]);
    setSelectedVersionIndex(null);
    if (version.spaceId) setActiveSpaceId(version.spaceId);
  }, []);

  const startNewSpace = useCallback(() => {
    setFlow((prev) => ({
      ...prev,
      // Keep client + address; clear everything scoped to a single space.
      sourceImageUrl: null,
      imageSource: null,
      yardFeatures: [],
      conceptPrompt: "",
      selectedStyleReference: null,
      spaceId: null,
      spaceLabel: null,
    }));
    setGeneratedImageUrl(null);
    setSelectedVersionIndex(null);
  }, []);

  const resetFlow = useCallback(() => {
    setFlow(EMPTY_FLOW);
    setGeneratedImageUrl(null);
    setGeneratedVersions([]);
    setSelectedVersionIndex(null);
    setActiveSpaceId(null);
    setCurrentProjectId(null);
    setCurrentProject(null);
    setCurrentGenerationResult(null);
  }, []);

  const createYardSpace = useCallback(
    (
      projectId: string,
      input: { name: string; yardType: YardType },
    ): Promise<YfpYardSpace> =>
      api.post<YfpYardSpace>(`/yfp/projects/${projectId}/yard-spaces`, input),
    [],
  );

  const renameYardSpace = useCallback(
    (yardSpaceId: string, name: string): Promise<YfpYardSpace> =>
      api.put<YfpYardSpace>(`/yfp/yard-spaces/${yardSpaceId}`, { name }),
    [],
  );

  const createProject = useCallback(async (): Promise<YfpProject> => {
    if (!contractor) throw new Error("Not authenticated");

    setIsCreatingProject(true);
    try {
      let project = await api.post<YfpProject>(
        `/yfp/contractors/${contractor.id}/projects`,
        {
          clientName: flow.clientName || "Unnamed Client",
          clientEmail: flow.clientEmail || undefined,
          propertyAddress: flow.address,
          satelliteImageUrl: flow.sourceImageUrl || undefined,
          // The backend materializes the project's first yard space from this type.
          yardType: flow.yardType ?? DEFAULT_YARD_TYPE,
        },
      );

      // Adopt the auto-created first space, renaming it to the label the
      // contractor chose (e.g. "Side Yard") when it differs from the default.
      let firstSpace = project.yardSpaces[0] ?? null;
      const desiredName = flow.spaceLabel?.trim();
      if (firstSpace && desiredName && desiredName !== firstSpace.name) {
        const renamed = await renameYardSpace(firstSpace.id, desiredName);
        firstSpace = renamed;
        project = {
          ...project,
          yardSpaces: project.yardSpaces.map((space, index) =>
            index === 0 ? renamed : space,
          ),
        };
      }
      if (firstSpace) {
        const space = firstSpace;
        setFlow((prev) => ({
          ...prev,
          spaceId: space.id,
          spaceLabel: space.name,
        }));
      }

      setCurrentProjectId(project.id);
      setCurrentProject(project);
      return project;
    } finally {
      setIsCreatingProject(false);
    }
  }, [
    contractor,
    flow.clientName,
    flow.clientEmail,
    flow.address,
    flow.sourceImageUrl,
    flow.yardType,
    flow.spaceLabel,
    renameYardSpace,
  ]);

  const createGenerationResult = useCallback(
    async (
      projectId: string,
      options?: CreateGenerationResultOptions,
    ): Promise<YfpGenerationResult> => {
      const result = await api.post<YfpGenerationResult>(
        "/yfp/generation-results",
        {
          projectId,
          yardSpaceId: options?.yardSpaceId,
          styleReferenceId: options?.styleReferenceId,
          parentGenerationResultId: options?.parentGenerationResultId,
          prompt: options?.prompt,
          conversationId: options?.conversationId,
          yardFeatures: options?.yardFeatures,
          sourceImageUrl: options?.sourceImageUrl,
        },
      );
      setCurrentGenerationResult(result);
      setCurrentProject((prev) => upsertGenerationResult(prev, result));
      return result;
    },
    [],
  );

  const updateGenerationResult = useCallback(
    async (
      id: string,
      patch: UpdateGenerationResultPatch,
    ): Promise<YfpGenerationResult> => {
      const result = await api.put<YfpGenerationResult>(
        `/yfp/generation-results/${id}`,
        patch,
      );
      setCurrentGenerationResult(result);
      setCurrentProject((prev) => upsertGenerationResult(prev, result));
      return result;
    },
    [],
  );

  const fetchProject = useCallback(
    (projectId: string): Promise<YfpProject> =>
      api.get<YfpProject>(`/yfp/projects/${projectId}`),
    [],
  );

  const setActiveProject = useCallback((project: YfpProject) => {
    setCurrentProject(project);
    setCurrentProjectId(project.id);
  }, []);

  return (
    <FlowContext.Provider
      value={{
        hasHydrated,
        clientName: flow.clientName,
        clientEmail: flow.clientEmail,
        address: flow.address,
        yardType: flow.yardType,
        sourceImageUrl: flow.sourceImageUrl,
        imageSource: flow.imageSource,
        yardFeatures: flow.yardFeatures,
        conceptPrompt: flow.conceptPrompt,
        selectedStyleReference: flow.selectedStyleReference,
        spaceId: flow.spaceId,
        spaceLabel: flow.spaceLabel,
        activeSpaceId,
        setActiveSpaceId,
        startNewSpace,
        ...setters,
        generatedImageUrl,
        setGeneratedImageUrl,
        generatedVersions,
        setGeneratedVersions,
        addGeneratedVersion,
        selectedVersionIndex,
        setSelectedVersionIndex,
        resetFlow,
        currentProjectId,
        currentProject,
        currentGenerationResult,
        isCreatingProject,
        createProject,
        createYardSpace,
        renameYardSpace,
        createGenerationResult,
        updateGenerationResult,
        fetchProject,
        setActiveProject,
      }}
    >
      {children}
    </FlowContext.Provider>
  );
}

export function useFlow() {
  const ctx = useContext(FlowContext);
  if (!ctx) throw new Error("useFlow must be used inside FlowProvider");
  return ctx;
}
